/**
 * BrandApiClient — Injectable API client for the brand editor SDK.
 * Consumers provide their own auth (headers, fetch function, or token).
 * Contributor: ben@hapi.vc
 */
export class BrandApiClient {
  constructor({ baseUrl, headers = {}, fetchFn, timeout = 120000 }) {
    this.baseUrl = baseUrl?.replace(/\/$/, '') || '';
    this.headers = headers;
    this.customFetch = fetchFn;
    this.timeout = timeout;
  }

  async _request(method, path, body, extraOpts = {}) {
    if (this.customFetch) {
      return this.customFetch(path, { method, body, ...extraOpts });
    }

    const url = `${this.baseUrl}${path}`;
    const opts = {
      method,
      headers: { 'Content-Type': 'application/json', ...this.headers },
      ...extraOpts,
    };
    if (body && method !== 'GET') {
      opts.body = body instanceof FormData ? body : JSON.stringify(body);
      if (body instanceof FormData) delete opts.headers['Content-Type'];
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), extraOpts.timeout || this.timeout);
    opts.signal = controller.signal;

    try {
      const res = await fetch(url, opts);
      clearTimeout(timer);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || err.message || `API error ${res.status}`);
      }
      return res.json();
    } catch (err) {
      clearTimeout(timer);
      throw err;
    }
  }

  // ─── Brand CRUD ─────────────────────────────────────────
  createBrand(data) { return this._request('POST', '', data); }
  getBrand() { return this._request('GET', ''); }
  deleteBrand() { return this._request('DELETE', ''); }
  importBrand(formData) { return this._request('POST', '/import', formData, { timeout: 120000 }); }

  // ─── Brainstorm & Style ─────────────────────────────────
  brainstorm(answers, count = 5) { return this._request('POST', '/brainstorm', { answers, count }, { timeout: 180000 }); }
  selectStyle(data) { return this._request('POST', '/style', data); }
  updateStyle(data) { return this._request('PATCH', '/style', data); }

  // ─── Assets ─────────────────────────────────────────────
  generateAssets(assetTypes, options) { return this._request('POST', '/assets', { asset_types: assetTypes, options }, { timeout: 180000 }); }
  getAssets(filters = {}) {
    const params = new URLSearchParams();
    if (filters.type) params.set('type', filters.type);
    if (filters.status) params.set('status', filters.status);
    const qs = params.toString();
    return this._request('GET', `/assets${qs ? '?' + qs : ''}`);
  }
  refineAsset(assetId, feedback) { return this._request('POST', `/assets/${assetId}/refine`, { feedback }, { timeout: 180000 }); }
  approveAsset(assetId) { return this._request('PATCH', `/assets/${assetId}`, { status: 'approved' }); }
  rejectAsset(assetId) { return this._request('PATCH', `/assets/${assetId}`, { status: 'rejected' }); }

  // ─── Jobs ───────────────────────────────────────────────
  pollJob(jobId) { return this._request('GET', `/jobs/${jobId}`); }

  // ─── Apply & Export ─────────────────────────────────────
  applyBrand() { return this._request('POST', '/apply'); }
  exportTokens() { return this._request('GET', '/tokens'); }
  exportCss() { return this._request('GET', '/export/css'); }
}
